// Pure helpers for the client-side expression tree (PR 2b-1).
//
// The audience builder holds Include and Exclude state as recursive AND/OR
// trees of variable leaves. These functions create, query, and mutate those
// trees immutably so the reducer stays simple.
//
// The shape this module operates on (see types/dataDictionary.d.ts):
//   ExpressionNode = ExpressionLeaf | ExpressionGroup
//   ExpressionLeaf  = { type: 'leaf'; nodeId; variable: VariableState }
//   ExpressionGroup = { type: 'and' | 'or'; nodeId; children: ExpressionNode[] }
//
// `nodeId` is a transient client-side identifier — it does NOT travel to the
// server. The server-side AST has no nodeId. See `apiTree.ts` for serialization.

import type {
  AudienceLeaf,
  ExpressionGroup,
  ExpressionLeaf,
  ExpressionNode,
  FileLeaf,
  MembershipLeaf,
  MembershipResourceType,
  ModelLeaf,
  VariableLeaf,
  VariableState,
  ZipLeaf,
} from '../types/dataDictionary';

// ---------------------------------------------------------------------------
// ATAD-146: model score range
// ---------------------------------------------------------------------------

/** CMDA model scores run 1–20; 1 is the best. Mirrors server/services/modelFiles.js. */
export const MODEL_SCORE_MIN = 1;
export const MODEL_SCORE_MAX = 20;

const isModelScore = (n: unknown): n is number =>
  Number.isInteger(n) && (n as number) >= MODEL_SCORE_MIN && (n as number) <= MODEL_SCORE_MAX;

/**
 * True for a range the server accepts: both ends whole numbers within 1–20
 * and min ≤ max. A model leaf without one must never reach the wire.
 */
export function isValidModelScoreRange(scoreMin: unknown, scoreMax: unknown): boolean {
  return isModelScore(scoreMin) && isModelScore(scoreMax) && scoreMin <= scoreMax;
}

/**
 * A score range as the UI shows it: best (1) on the right, e.g. "9–3", to
 * match ModelCard's reversed slider. Display only; data stays min/max.
 */
export function formatModelScoreRange(scoreMin: number, scoreMax: number): string {
  return `${scoreMax}–${scoreMin}`;
}

/** True when a model leaf's range is the whole 1–20 scale — i.e. everyone. */
export function isFullModelScoreRange(leaf: Pick<ModelLeaf, 'scoreMin' | 'scoreMax'>): boolean {
  return leaf.scoreMin <= MODEL_SCORE_MIN && leaf.scoreMax >= MODEL_SCORE_MAX;
}

// ---------------------------------------------------------------------------
// nodeId generator
// ---------------------------------------------------------------------------

let _nodeIdCounter = 0;

/**
 * Generate a unique transient nodeId. Not cryptographically random — the only
 * requirement is uniqueness within a single browser tab session, which a
 * monotonic counter satisfies. Test suites can reset by importing
 * `__resetNodeIdCounterForTests` from this module.
 */
export function nextNodeId(): string {
  _nodeIdCounter += 1;
  return `n${_nodeIdCounter}`;
}

/** Test-only: reset the counter so snapshots remain stable across runs. */
export function __resetNodeIdCounterForTests(): void {
  _nodeIdCounter = 0;
}

// ---------------------------------------------------------------------------
// Constructors
// ---------------------------------------------------------------------------

export function createLeaf(variable: VariableState): VariableLeaf {
  return { type: 'leaf', kind: 'variable', nodeId: nextNodeId(), variable };
}

/** PR 2c: construct an audience-membership leaf. */
export function createAudienceLeaf(audience: {
  id: string;
  name: string;
}): AudienceLeaf {
  return {
    type: 'leaf',
    kind: 'audience',
    nodeId: nextNodeId(),
    audienceId: audience.id,
    name: audience.name,
  };
}

/** PR 2c: construct a file-membership leaf. */
export function createFileLeaf(file: { id: string; name: string }): FileLeaf {
  return {
    type: 'leaf',
    kind: 'file',
    nodeId: nextNodeId(),
    fileId: file.id,
    name: file.name,
  };
}

/** ATAD-136: construct a ZIP-code-file leaf. */
export function createZipLeaf(file: {
  id: string;
  name: string;
  zipCount?: number;
}): ZipLeaf {
  const leaf: ZipLeaf = {
    type: 'leaf',
    kind: 'zip',
    nodeId: nextNodeId(),
    fileId: file.id,
    name: file.name,
  };
  if (typeof file.zipCount === 'number') leaf.zipCount = file.zipCount;
  return leaf;
}

/**
 * ATAD-146: construct a model-file leaf. A freshly dropped model has no range
 * yet, so it starts at the full 1–20 scale (which the breadth warning flags
 * until the user narrows it). Pass a stored range to rebuild a saved leaf.
 */
export function createModelLeaf(file: {
  id: string;
  name: string;
  scoreMin?: number;
  scoreMax?: number;
}): ModelLeaf {
  return {
    type: 'leaf',
    kind: 'model',
    nodeId: nextNodeId(),
    fileId: file.id,
    name: file.name,
    scoreMin: file.scoreMin ?? MODEL_SCORE_MIN,
    scoreMax: file.scoreMax ?? MODEL_SCORE_MAX,
  };
}

/**
 * Construct the membership leaf for a dropped "my data" resource.
 *
 * ATAD-146: fails closed. An unknown type used to fall through to a file
 * leaf, so a new kind of resource silently became a seed-file filter.
 */
export function createMembershipLeaf(
  type: MembershipResourceType,
  resource: { id: string; name: string },
): MembershipLeaf {
  switch (type) {
    case 'audience':
      return createAudienceLeaf(resource);
    case 'file':
      return createFileLeaf(resource);
    case 'zip':
      return createZipLeaf(resource);
    case 'model':
      return createModelLeaf(resource);
    default:
      throw new Error(`Unknown kind of resource: "${type}".`);
  }
}

export function createGroup(
  op: 'and' | 'or',
  children: ExpressionNode[],
): ExpressionGroup {
  return { type: op, nodeId: nextNodeId(), children };
}

// ---------------------------------------------------------------------------
// Predicates
// ---------------------------------------------------------------------------

export function isLeaf(node: ExpressionNode): node is ExpressionLeaf {
  return node.type === 'leaf';
}

export function isGroup(node: ExpressionNode): node is ExpressionGroup {
  return node.type === 'and' || node.type === 'or';
}

/** True for variable leaves (kind 'variable' or absent — back-compat). */
export function isVariableLeaf(leaf: ExpressionLeaf): leaf is VariableLeaf {
  return !leaf.kind || leaf.kind === 'variable';
}

/** True for audience-membership leaves. */
export function isAudienceLeaf(leaf: ExpressionLeaf): leaf is AudienceLeaf {
  return leaf.kind === 'audience';
}

/** True for file-membership leaves. */
export function isFileLeaf(leaf: ExpressionLeaf): leaf is FileLeaf {
  return leaf.kind === 'file';
}

/** ATAD-136: true for ZIP-code-file leaves. */
export function isZipLeaf(leaf: ExpressionLeaf): leaf is ZipLeaf {
  return leaf.kind === 'zip';
}

/** ATAD-146: true for CMDA model-file leaves. */
export function isModelLeaf(leaf: ExpressionLeaf): leaf is ModelLeaf {
  return leaf.kind === 'model';
}

/**
 * True for any membership (audience, file, ZIP file or model file) leaf.
 * Model leaves are membership leaves with a score range: renderers must check
 * isModelLeaf first, or the range is never shown.
 */
export function isMembershipLeaf(leaf: ExpressionLeaf): leaf is MembershipLeaf {
  return (
    leaf.kind === 'audience' ||
    leaf.kind === 'file' ||
    leaf.kind === 'zip' ||
    leaf.kind === 'model'
  );
}

/** The id of the resource a membership leaf refers to. */
export function membershipLeafId(leaf: MembershipLeaf): string {
  return leaf.kind === 'audience' ? leaf.audienceId : leaf.fileId;
}

/** True when the tree is null or has no leaves anywhere. */
export function isEmpty(root: ExpressionNode | null): boolean {
  if (!root) return true;
  if (isLeaf(root)) return false;
  return root.children.every(isEmpty);
}

// ---------------------------------------------------------------------------
// Walking + querying
// ---------------------------------------------------------------------------

/** Visit every leaf in the tree (depth-first, left-to-right). */
export function walkLeaves(
  root: ExpressionNode | null,
  visit: (leaf: ExpressionLeaf) => void,
): void {
  if (!root) return;
  if (isLeaf(root)) {
    visit(root);
    return;
  }
  root.children.forEach((child) => walkLeaves(child, visit));
}

/** Collect every leaf into a flat array. Useful for UI that still renders flat. */
export function flattenLeaves(root: ExpressionNode | null): ExpressionLeaf[] {
  const out: ExpressionLeaf[] = [];
  walkLeaves(root, (leaf) => out.push(leaf));
  return out;
}

/**
 * Convenience for legacy UI code that still works on flat `VariableState[]`
 * lists. Walks the appropriate zone's expression tree and returns the leaves'
 * variables in left-to-right depth-first order.
 *
 * In PR 2b-1 the UI renders flat (no AND/OR controls yet), so the tree is
 * always a single root AND with leaves; this helper returns the leaf list.
 * PR 2b-2 will replace consumers with a recursive renderer.
 */
export function getZoneVariables<
  S extends {
    expression: ExpressionNode | null;
    excludeExpression: ExpressionNode | null;
  },
>(state: S, zoneType: 'include' | 'exclude'): VariableState[] {
  const tree = zoneType === 'include' ? state.expression : state.excludeExpression;
  return flattenLeaves(tree)
    .filter(isVariableLeaf)
    .map((leaf) => leaf.variable);
}

/**
 * PR 2c: collect membership leaves from a zone's tree. Used in canDrop /
 * dedupe logic to enforce "no duplicate audience or file in either zone".
 */
export function getZoneMembershipLeaves<
  S extends {
    expression: ExpressionNode | null;
    excludeExpression: ExpressionNode | null;
  },
>(state: S, zoneType: 'include' | 'exclude'): MembershipLeaf[] {
  const tree = zoneType === 'include' ? state.expression : state.excludeExpression;
  return flattenLeaves(tree).filter(isMembershipLeaf);
}

/** Find the first VARIABLE leaf whose `variable.id` matches. */
export function findLeafByVariableId(
  root: ExpressionNode | null,
  variableId: string,
): VariableLeaf | null {
  if (!root) return null;
  if (isLeaf(root)) {
    if (isVariableLeaf(root) && root.variable.id === variableId) return root;
    return null;
  }
  for (const child of root.children) {
    const found = findLeafByVariableId(child, variableId);
    if (found) return found;
  }
  return null;
}

/** Find the first audience leaf with the given audienceId. */
export function findAudienceLeafByAudienceId(
  root: ExpressionNode | null,
  audienceId: string,
): AudienceLeaf | null {
  if (!root) return null;
  if (isLeaf(root)) {
    if (isAudienceLeaf(root) && root.audienceId === audienceId) return root;
    return null;
  }
  for (const child of root.children) {
    const found = findAudienceLeafByAudienceId(child, audienceId);
    if (found) return found;
  }
  return null;
}

/**
 * Find the first membership leaf of `type` for resource `id`. File, ZIP and
 * model leaves all carry a fileId, so the kind must match too.
 */
export function findMembershipLeaf(
  root: ExpressionNode | null,
  type: MembershipResourceType,
  id: string,
): MembershipLeaf | null {
  if (!root) return null;
  if (isLeaf(root)) {
    if (isMembershipLeaf(root) && root.kind === type && membershipLeafId(root) === id) {
      return root;
    }
    return null;
  }
  for (const child of root.children) {
    const found = findMembershipLeaf(child, type, id);
    if (found) return found;
  }
  return null;
}

/** Find the first file leaf with the given fileId. */
export function findFileLeafByFileId(
  root: ExpressionNode | null,
  fileId: string,
): FileLeaf | null {
  if (!root) return null;
  if (isLeaf(root)) {
    if (isFileLeaf(root) && root.fileId === fileId) return root;
    return null;
  }
  for (const child of root.children) {
    const found = findFileLeafByFileId(child, fileId);
    if (found) return found;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Immutable mutations
// ---------------------------------------------------------------------------

/**
 * Append a leaf to the root. If the tree is empty, the leaf becomes the root.
 * Otherwise the root is normalized to an AND group with the new leaf appended.
 */
export function addLeafToRoot(
  root: ExpressionNode | null,
  leaf: ExpressionLeaf,
): ExpressionNode {
  if (!root) return leaf;
  if (isGroup(root) && root.type === 'and') {
    return { ...root, children: [...root.children, leaf] };
  }
  // Existing root is a leaf or an OR group — wrap in a fresh AND.
  return createGroup('and', [root, leaf]);
}

/**
 * Remove every VARIABLE leaf whose `variable.id` matches. Membership leaves
 * are unaffected. Empty groups left behind are pruned.
 */
export function removeLeavesByVariableId(
  root: ExpressionNode | null,
  variableId: string,
): ExpressionNode | null {
  if (!root) return null;
  if (isLeaf(root)) {
    if (isVariableLeaf(root) && root.variable.id === variableId) return null;
    return root;
  }
  const newChildren = root.children
    .map((c) => removeLeavesByVariableId(c, variableId))
    .filter((c): c is ExpressionNode => c !== null);
  return collapseGroup(root.type, newChildren, root.nodeId);
}

/**
 * Remove every VARIABLE leaf whose `variable.id` starts with the given
 * category prefix (e.g. `"DemoIQ"`). Mirrors the legacy REMOVE_CATEGORY
 * action. Membership leaves are unaffected.
 */
export function removeLeavesByCategoryKey(
  root: ExpressionNode | null,
  categoryKey: string,
): ExpressionNode | null {
  if (!root) return null;
  if (isLeaf(root)) {
    if (isVariableLeaf(root) && root.variable.id.startsWith(categoryKey)) {
      return null;
    }
    return root;
  }
  const newChildren = root.children
    .map((c) => removeLeavesByCategoryKey(c, categoryKey))
    .filter((c): c is ExpressionNode => c !== null);
  return collapseGroup(root.type, newChildren, root.nodeId);
}

/**
 * Update the `variable.state` field of the matching VARIABLE leaf.
 * Membership leaves are unaffected.
 */
export function updateLeafVariableState(
  root: ExpressionNode | null,
  variableId: string,
  newState: VariableState['state'],
): ExpressionNode | null {
  if (!root) return null;
  if (isLeaf(root)) {
    if (!isVariableLeaf(root) || root.variable.id !== variableId) return root;
    return { ...root, variable: { ...root.variable, state: newState } };
  }
  return {
    ...root,
    children: root.children.map(
      (c) => updateLeafVariableState(c, variableId, newState) as ExpressionNode,
    ),
  };
}

/**
 * ATAD-146: set the score range of the model leaf with `nodeId` (not by
 * fileId: the same model can sit in a zone twice with different ranges).
 *
 * The range is normalized to whole numbers within 1–20, low end first, so a
 * typed or dragged value can't put an invalid range on the wire. A bound that
 * isn't a number keeps the leaf's current value.
 *
 * Returns the SAME reference when the leaf is missing, isn't a model leaf, or
 * already has that range — the reducer uses that to tell a real edit from
 * Range reporting the value it was mounted with.
 */
export function updateModelLeafRange(
  root: ExpressionNode | null,
  nodeId: string,
  range: { min?: number | null; max?: number | null } | null | undefined,
): ExpressionNode | null {
  if (!root) return root;
  const target = findNodeById(root, nodeId);
  if (!target || !isLeaf(target) || !isModelLeaf(target)) return root;

  const toScore = (value: unknown, fallback: number) => {
    const n = Number(value);
    if (value == null || !Number.isFinite(n)) return fallback;
    return Math.min(MODEL_SCORE_MAX, Math.max(MODEL_SCORE_MIN, Math.round(n)));
  };
  const a = toScore(range?.min, target.scoreMin);
  const b = toScore(range?.max, target.scoreMax);
  const scoreMin = Math.min(a, b);
  const scoreMax = Math.max(a, b);

  if (scoreMin === target.scoreMin && scoreMax === target.scoreMax) return root;
  return replaceNodeById(root, nodeId, { ...target, scoreMin, scoreMax });
}

/** Map a function over every leaf, returning a new tree (or null). */
export function mapLeaves(
  root: ExpressionNode | null,
  fn: (leaf: ExpressionLeaf) => ExpressionLeaf,
): ExpressionNode | null {
  if (!root) return null;
  if (isLeaf(root)) return fn(root);
  return { ...root, children: root.children.map((c) => mapLeaves(c, fn) as ExpressionNode) };
}

// ---------------------------------------------------------------------------
// PR 2b-2 mutations: toggle operator, wrap two leaves into a new group, ungroup
// ---------------------------------------------------------------------------

/**
 * Flip a group's operator (AND ↔ OR) by `nodeId`. Leaves are unaffected.
 * If `nodeId` doesn't match any group, returns the SAME reference unchanged
 * (preserves React `useMemo` invalidation behavior).
 */
export function toggleNodeOperator(
  root: ExpressionNode | null,
  nodeId: string,
): ExpressionNode | null {
  if (!root) return null;
  // Short-circuit: only mutate if nodeId points to a group somewhere.
  const target = findNodeById(root, nodeId);
  if (!target || isLeaf(target)) return root;
  return toggleNodeOperatorInner(root, nodeId);
}

function toggleNodeOperatorInner(
  root: ExpressionNode,
  nodeId: string,
): ExpressionNode {
  if (isLeaf(root)) return root;
  if (root.nodeId === nodeId) {
    return { ...root, type: root.type === 'and' ? 'or' : 'and' };
  }
  return {
    ...root,
    children: root.children.map((c) => toggleNodeOperatorInner(c, nodeId)),
  };
}

/**
 * Wrap two existing nodes (identified by their nodeIds) into a new group.
 * The source node is removed from its current position and the target node
 * is replaced with `createGroup(op, [target, source])`. Used by the
 * drag-to-group gesture in PR 2b-3 and exposed earlier so unit tests can
 * cover the tree-mutation surface.
 *
 * If either nodeId is missing, the tree is returned unchanged.
 *
 * Important: source and target must not be the same node, and target must
 * not be a descendant of source (would orphan the source's children).
 * Both invariants are checked; on violation the tree is returned unchanged.
 */
export function wrapNodesInGroup(
  root: ExpressionNode | null,
  sourceNodeId: string,
  targetNodeId: string,
  op: 'and' | 'or',
): ExpressionNode | null {
  if (!root) return null;
  if (sourceNodeId === targetNodeId) return root;

  const source = findNodeById(root, sourceNodeId);
  const target = findNodeById(root, targetNodeId);
  if (!source || !target) return root;

  // Reject if target is inside source — would create an orphaned subtree.
  if (containsNodeId(source, targetNodeId)) return root;

  // 1. Remove source from its current location.
  const withoutSource = removeNodeById(root, sourceNodeId);
  if (!withoutSource) return root;

  // 2. Replace target with a fresh group [target, source].
  return replaceNodeById(
    withoutSource,
    targetNodeId,
    createGroup(op, [target, source]),
  );
}

/**
 * Append a leaf as a new last child of the group identified by `groupNodeId`.
 * Used by drag-into-existing-group: dropping a fresh attribute on a group
 * appends it as a new sibling of the group's existing children.
 *
 * If `groupNodeId` doesn't match a group, the tree is returned unchanged.
 */
export function addLeafToGroup(
  root: ExpressionNode | null,
  groupNodeId: string,
  leaf: ExpressionLeaf,
): ExpressionNode | null {
  if (!root) return null;
  const target = findNodeById(root, groupNodeId);
  if (!target || isLeaf(target)) return root;
  return addLeafToGroupInner(root, groupNodeId, leaf);
}

function addLeafToGroupInner(
  root: ExpressionNode,
  groupNodeId: string,
  leaf: ExpressionLeaf,
): ExpressionNode {
  if (isLeaf(root)) return root;
  if (root.nodeId === groupNodeId) {
    return { ...root, children: [...root.children, leaf] };
  }
  return {
    ...root,
    children: root.children.map((c) => addLeafToGroupInner(c, groupNodeId, leaf)),
  };
}

/**
 * Move an existing leaf identified by `sourceNodeId` into the group
 * identified by `groupNodeId`. Used by drag-an-existing-card-into-a-group.
 *
 * Implementation: remove the source from its current location, then append
 * to the group. Auto-collapse handles any group that loses its second-to-
 * last child as a result of the move.
 *
 * Returns the tree unchanged when:
 *   - either nodeId doesn't exist,
 *   - source is not a leaf,
 *   - target is not a group,
 *   - source is already a direct child of the target group, OR
 *   - source is the target group itself (would self-nest).
 */
export function moveLeafIntoGroup(
  root: ExpressionNode | null,
  sourceNodeId: string,
  groupNodeId: string,
): ExpressionNode | null {
  if (!root) return null;
  if (sourceNodeId === groupNodeId) return root;

  const source = findNodeById(root, sourceNodeId);
  const target = findNodeById(root, groupNodeId);
  if (!source || !isLeaf(source)) return root;
  if (!target || isLeaf(target)) return root;

  // Already inside this group as a direct child → no-op.
  if (target.children.some((c) => c.nodeId === sourceNodeId)) return root;

  // Target must not be inside source — but source is a leaf, so this can't
  // happen. Skip the check.

  const withoutSource = removeNodeById(root, sourceNodeId);
  if (!withoutSource) return root;

  // After removal, the group might have collapsed away (e.g., source was a
  // sibling of a single-child group whose other child was source's parent).
  // Re-resolve the group by id; if it's gone, the tree changed shape and we
  // bail on the move.
  if (!findNodeById(withoutSource, groupNodeId)) return root;

  return addLeafToGroup(withoutSource, groupNodeId, source) as ExpressionNode;
}

/**
 * Wrap an existing tree node with a fresh leaf. Used by drag-from-dictionary-
 * onto-an-existing-card: the dragged attribute becomes a new leaf, and the
 * target node is replaced with `createGroup(op, [target, newLeaf])`.
 *
 * Differs from `wrapNodesInGroup` because the source node doesn't exist in
 * the tree yet. Both nodes end up as direct children of the new group.
 *
 * If `targetNodeId` doesn't match any node, the tree is returned unchanged.
 */
export function wrapNodeWithFreshLeaf(
  root: ExpressionNode | null,
  targetNodeId: string,
  newLeaf: ExpressionLeaf,
  op: 'and' | 'or',
): ExpressionNode | null {
  if (!root) return newLeaf;
  const target = findNodeById(root, targetNodeId);
  if (!target) return root;
  return replaceNodeById(root, targetNodeId, createGroup(op, [target, newLeaf]));
}

/**
 * Replace a group with its children inline (splice up). The group itself
 * disappears; its children move to its parent's children list at the same
 * position. If the group is the root, its children become the new root —
 * collapsing to a single child if there's only one.
 *
 * Returns the SAME reference unchanged when:
 *   - `nodeId` doesn't match any node, OR
 *   - the matched node is a leaf, OR
 *   - the matched node is the root and has multiple children (no parent to
 *     splice into).
 */
export function ungroupById(
  root: ExpressionNode | null,
  nodeId: string,
): ExpressionNode | null {
  if (!root) return null;
  const target = findNodeById(root, nodeId);
  if (!target || isLeaf(target)) return root;

  // Root case. TypeScript needs an explicit narrow even though we already
  // returned early when `target` (which is `root` here) is a leaf.
  if (root.nodeId === nodeId) {
    if (isLeaf(root)) return root;
    if (root.children.length === 0) return null;
    if (root.children.length === 1) return root.children[0];
    return root; // multi-child root: no-op (no parent to splice into).
  }

  return ungroupByIdInner(root, nodeId);
}

function ungroupByIdInner(
  root: ExpressionNode,
  nodeId: string,
): ExpressionNode {
  if (isLeaf(root)) return root;
  const newChildren: ExpressionNode[] = [];
  for (const child of root.children) {
    if (child.nodeId === nodeId && isGroup(child)) {
      newChildren.push(...child.children);
    } else {
      newChildren.push(ungroupByIdInner(child, nodeId));
    }
  }
  return { ...root, children: newChildren };
}

// ---------------------------------------------------------------------------
// Internal helpers used by wrapNodesInGroup / ungroupById
// ---------------------------------------------------------------------------

/** Find any node (leaf or group) by nodeId. */
export function findNodeById(
  root: ExpressionNode | null,
  nodeId: string,
): ExpressionNode | null {
  if (!root) return null;
  if (root.nodeId === nodeId) return root;
  if (isLeaf(root)) return null;
  for (const child of root.children) {
    const found = findNodeById(child, nodeId);
    if (found) return found;
  }
  return null;
}

/** Returns true if any node in `root`'s subtree has the given nodeId. */
function containsNodeId(root: ExpressionNode, nodeId: string): boolean {
  return findNodeById(root, nodeId) !== null;
}

/** Remove any node by nodeId. Empty/single-child groups left behind collapse. */
export function removeNodeById(
  root: ExpressionNode | null,
  nodeId: string,
): ExpressionNode | null {
  if (!root) return null;
  if (root.nodeId === nodeId) return null;
  if (isLeaf(root)) return root;
  const newChildren = root.children
    .map((c) => removeNodeById(c, nodeId))
    .filter((c): c is ExpressionNode => c !== null);
  return collapseGroup(root.type, newChildren, root.nodeId);
}

/** Replace any node (by nodeId) with `replacement`. Returns the new tree. */
function replaceNodeById(
  root: ExpressionNode,
  nodeId: string,
  replacement: ExpressionNode,
): ExpressionNode {
  if (root.nodeId === nodeId) return replacement;
  if (isLeaf(root)) return root;
  return {
    ...root,
    children: root.children.map((c) => replaceNodeById(c, nodeId, replacement)),
  };
}

// ---------------------------------------------------------------------------
// Group collapse helper
// ---------------------------------------------------------------------------

/**
 * After children have been removed from a group, decide whether the group
 * should disappear (no children), unwrap (single child), or stay as-is.
 *
 *   0 children  → null (group disappears)
 *   1 child     → that child (group is functionally equivalent to no group)
 *   2+ children → preserve the group
 */
function collapseGroup(
  op: 'and' | 'or',
  children: ExpressionNode[],
  preserveNodeId: string,
): ExpressionNode | null {
  if (children.length === 0) return null;
  if (children.length === 1) return children[0];
  return { type: op, nodeId: preserveNodeId, children };
}
